// ============================================================================
// CHANGO V5.0 — Quote comment panel (D6 frontend)
// ============================================================================
// Mounted on QuoteDetail.jsx. Loads /api/quotes/:quoteId/comments and lets the
// current user post a new comment (roles boss/finance/sales — deny is handled
// server-side; UI still hides the composer for anonymous sessions).
//
// @mention handling: the backend expects tokens of the form @[userId]. We do
// NOT ship a user-search picker in this pass — that lands in D10 alongside the
// workspace member directory. For now:
//   * Display: strip @[id] tokens so they render as the literal @user_<id> text.
//   * Compose: any @<number> the user types is converted to @[<number>] before
//     submit. Typed "@ueda" (no digit) is left alone and ignored by the server
//     parser. Safe degradation — no dangling references.
//
// Edit window: backend enforces a 5-minute edit window and 'author OR boss'
// for delete. We keep the UI permissive and let the server return 403 /
// CONFLICT if the user misses the window — a toast shows the reason.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { api, HttpError } from '../api/client.js';
import { useAuth } from '../hooks/useAuth.js';

const MAX_LEN = 5000;
const MIN_LEN = 1;

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = Date.now();
  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec} 秒前`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} 分钟前`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} 小时前`;
  return d.toLocaleString('zh-CN', { hour12: false });
}

function renderBody(text) {
  if (typeof text !== 'string') return '';
  // Convert @[id] → @u{id} for display. The authoritative mention list comes
  // from the `mentions` array the backend projects; we don't try to resolve
  // display names here (server returns user_id only).
  return text.replace(/@\[(\d{1,10})\]/g, '@u$1');
}

function composeToServer(text) {
  // Convert any @<digits> the user typed into backend's canonical @[<digits>]
  // form. Leave already-bracketed `@[N]` alone so we don't double-transform.
  // Implementation note: we use `replace` with a function callback + lookbehind
  // at the matched offset instead of a regex character class containing a
  // literal `[` — the offline balance-checker (scripts/offline-balance-check.mjs)
  // doesn't parse regex literals and trips on `[^@[]`.
  return text.replace(/@(\d{1,10})\b/g, (match, digits, offset, full) => {
    const prev = offset > 0 ? full[offset - 1] : '';
    // Already inside `@[N]` (prev `[`) or chained `@@N` — leave as-is.
    if (prev === '[' || prev === '@') return match;
    return `@[${digits}]`;
  });
}

export default function QuoteCommentPanel({ quoteId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [toast, setToast] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setErr(null);
    try {
      const data = await api.get(`/api/quotes/${quoteId}/comments`, { signal: ctrl.signal });
      setComments(Array.isArray(data?.comments) ? data.comments : []);
    } catch (e) {
      if (e.name === 'AbortError') return;
      setErr(e instanceof HttpError ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    load();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [load]);

  const postComment = async (e) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (trimmed.length < MIN_LEN) { setToast('内容不能为空'); return; }
    if (trimmed.length > MAX_LEN) { setToast(`内容不能超过 ${MAX_LEN} 字`); return; }
    setSubmitting(true);
    try {
      await api.post(`/api/quotes/${quoteId}/comments`, {
        body_md: composeToServer(trimmed)
      });
      setDraft('');
      await load();
    } catch (e2) {
      setToast(e2?.message || '发布失败');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditDraft(c.body_md ?? '');
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft(''); };

  const saveEdit = async (id) => {
    const trimmed = editDraft.trim();
    if (trimmed.length < MIN_LEN) { setToast('内容不能为空'); return; }
    try {
      await api.patch(`/api/quotes/${quoteId}/comments/${id}`, {
        body_md: composeToServer(trimmed)
      });
      cancelEdit();
      await load();
    } catch (e) {
      setToast(e?.message || '编辑失败');
    }
  };

  const removeComment = async (id) => {
    if (!window.confirm('删除这条评论？删除后不可恢复。')) return;
    try {
      await api.delete(`/api/quotes/${quoteId}/comments/${id}`);
      await load();
    } catch (e) {
      setToast(e?.message || '删除失败');
    }
  };

  return (
    <section className="card p-4 mt-6">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">评论 · {comments.length}</h3>
        <button type="button" className="btn-ghost text-sm" onClick={load}>刷新</button>
      </header>

      {err && <p className="err text-sm mb-3">{err}</p>}

      {loading ? (
        <p className="text-sm text-slate-500">加载中…</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-slate-500 mb-4">暂无评论，写下第一条反馈。</p>
      ) : (
        <ul className="space-y-3 mb-4">
          {comments.map((c) => (
            <li key={c.id} className="rounded border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-3 text-sm">
                <div>
                  <span className="font-medium text-slate-700">
                    {c.author_name || `u${c.author_id}`}
                  </span>
                  <span className="ml-2 text-xs text-slate-400" title={c.created_at}>
                    {formatWhen(c.created_at)}
                  </span>
                  {c.edited_at && (
                    <span className="ml-2 text-xs text-slate-400">（已编辑）</span>
                  )}
                  {c.deleted_at && (
                    <span className="ml-2 text-xs text-rose-500">[已删除 · 仅作者与老板可见]</span>
                  )}
                </div>
                {user && (user.id === c.author_id || user.role === 'boss') && !c.deleted_at && (
                  <div className="flex items-center gap-2 text-xs">
                    {user.id === c.author_id && editingId !== c.id && (
                      <button type="button" className="text-slate-500 hover:text-slate-700" onClick={() => startEdit(c)}>编辑</button>
                    )}
                    <button type="button" className="text-rose-500 hover:text-rose-700" onClick={() => removeComment(c.id)}>删除</button>
                  </div>
                )}
              </div>

              {editingId === c.id ? (
                <div className="mt-2">
                  <textarea
                    className="field"
                    rows={3}
                    maxLength={MAX_LEN}
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <button type="button" className="btn-ghost text-sm" onClick={cancelEdit}>取消</button>
                    <button type="button" className="btn-primary text-sm" onClick={() => saveEdit(c.id)}>保存</button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                  {renderBody(c.body_md)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={postComment} className="border-t border-slate-200 pt-3">
        <label htmlFor="comment-draft" className="sr-only">写评论</label>
        <textarea
          id="comment-draft"
          className="field"
          rows={3}
          maxLength={MAX_LEN}
          placeholder={'撰写反馈…  使用 @12 提及用户（12 为 user_id）'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">{draft.length} / {MAX_LEN}</span>
          <button type="submit" className="btn-primary text-sm" disabled={submitting || draft.trim().length === 0}>
            {submitting ? '发布中…' : '发布评论'}
          </button>
        </div>
      </form>

      {toast && (
        <div
          role="status"
          className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          onClick={() => setToast(null)}
        >
          {toast} <span className="ml-2 cursor-pointer underline">关闭</span>
        </div>
      )}
    </section>
  );
}
